import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Facebook, Send, Loader2 } from "lucide-react";
import { BRAND, SERVICES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  service: "",
  message: "",
};

const inputClass =
  "mt-2 rounded-none bg-transparent border-0 border-b border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-12 px-0";

export const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Message sent. I'll be in touch shortly.");
      setForm(initialState);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 bg-[#09090B] border-t border-zinc-800"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left: Info */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-[2px] w-12 bg-[#D4AF37]" />
            <p className="font-display text-[#D4AF37] text-sm font-bold tracking-[0.3em] uppercase">
              Get In Touch
            </p>
          </div>
          <h2
            data-testid="contact-heading"
            className="font-display text-white font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl"
          >
            Ready to <br />
            <span className="text-[#D4AF37]">start training?</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed font-light max-w-md">
            Drop your details and I'll personally get back to you within 24
            hours to discuss goals, availability and your first session.
          </p>

          <ul className="mt-12 space-y-6">
            <li className="flex items-start gap-4">
              <div className="h-11 w-11 border border-zinc-800 flex items-center justify-center">
                <Phone size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase">
                  Phone
                </p>
                <a
                  href={BRAND.phoneHref}
                  data-testid="contact-phone-link"
                  className="font-display text-white text-xl font-bold tracking-wide hover:text-[#D4AF37] transition-colors"
                >
                  {BRAND.phone}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-11 w-11 border border-zinc-800 flex items-center justify-center">
                <Mail size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase">
                  Email
                </p>
                <a
                  href={BRAND.emailHref}
                  data-testid="contact-email-link"
                  className="font-display text-white text-xl font-bold tracking-wide hover:text-[#D4AF37] transition-colors break-all"
                >
                  {BRAND.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="h-11 w-11 border border-zinc-800 flex items-center justify-center">
                <MapPin size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] font-semibold tracking-[0.25em] uppercase">
                  Location
                </p>
                <p className="font-display text-white text-xl font-bold tracking-wide">
                  {BRAND.location}
                </p>
              </div>
            </li>
          </ul>

          <div className="mt-12 flex items-center gap-4">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-link"
              className="h-11 w-11 border border-zinc-800 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] text-white transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a
              href={BRAND.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-facebook-link"
              className="h-11 w-11 border border-zinc-800 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] text-white transition-colors duration-300"
              aria-label="Facebook"
            >
              <Facebook size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Right: Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 p-8 lg:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <Label
                  htmlFor="name"
                  className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500"
                >
                  Name
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handleChange("name")}
                  placeholder="Your full name"
                  className={inputClass}
                />
              </div>
              <div>
                <Label
                  htmlFor="email"
                  className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500"
                >
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={handleChange("email")}
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>
              <div>
                <Label
                  htmlFor="phone"
                  className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500"
                >
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  placeholder="Optional"
                  className={inputClass}
                />
              </div>
              <div>
                <Label
                  htmlFor="service"
                  className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500"
                >
                  Service Interest
                </Label>
                <Select value={form.service} onValueChange={handleChange("service")}>
                  <SelectTrigger
                    id="service"
                    data-testid="contact-select-service"
                    className="mt-2 rounded-none bg-transparent border-0 border-b border-zinc-800 focus:ring-0 focus:border-[#D4AF37] text-white h-12 px-0"
                  >
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-900 border-zinc-800 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.title}
                        data-testid={`contact-select-option-${s.id}`}
                        className="rounded-none focus:bg-[#D4AF37]/20 focus:text-white"
                      >
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-7">
              <Label
                htmlFor="message"
                className="text-[10px] font-semibold tracking-[0.22em] uppercase text-zinc-500"
              >
                Message
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={handleChange("message")}
                rows={5}
                placeholder="Tell me about your goals, experience, availability..."
                className="mt-2 rounded-none bg-transparent border-0 border-b border-zinc-800 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white px-0"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-10 inline-flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#F3CE56] disabled:opacity-60 text-zinc-950 px-9 py-4 font-display text-base font-bold uppercase tracking-[0.16em] transition-colors duration-300 w-full md:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Enquiry
                  <Send size={15} strokeWidth={2} />
                </>
              )}
            </button>

            <p className="mt-4 text-zinc-600 text-xs font-light">
              By submitting you consent to JE Training contacting you about your enquiry.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
